// Which release notes the "what's new" popup shows. Versions are the release tags publish.ps1
// builds with (`0.2.2-beta`, `1.0`, `1.0.0`, optionally with a leading `v`), ordered like semver:
// a pre-release comes before its release (`1.0.0-beta` < `1.0.0`).

interface ParsedVersion {
    /** Major, minor, patch. */
    core: [number, number, number];
    /** The dot-separated identifiers after `-`; empty for a release. */
    prerelease: string[];
}

const VERSION_PATTERN = /^v?(\d+)\.(\d+)(?:\.(\d+))?(?:-([0-9A-Za-z.-]+))?$/;
const NUMERIC = /^\d+$/;

function parseVersion(text: string): ParsedVersion | undefined {
    const match = VERSION_PATTERN.exec(text.trim());
    if (!match) return undefined;
    const [, major, minor, patch = '0', prerelease] = match;
    return {
        core: [Number(major), Number(minor), Number(patch)],
        prerelease: prerelease ? prerelease.split('.') : [],
    };
}

function compareIdentifiers(a: string, b: string): number {
    const aNumeric = NUMERIC.test(a);
    const bNumeric = NUMERIC.test(b);
    if (aNumeric && bNumeric) return Number(a) - Number(b);
    if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
    return a < b ? -1 : a > b ? 1 : 0;
}

function compareParsed(a: ParsedVersion, b: ParsedVersion): number {
    for (let i = 0; i < 3; i++) {
        if (a.core[i] !== b.core[i]) return a.core[i] - b.core[i];
    }
    // A release sorts after its pre-releases.
    if (!a.prerelease.length || !b.prerelease.length) return b.prerelease.length - a.prerelease.length;
    for (let i = 0; i < Math.min(a.prerelease.length, b.prerelease.length); i++) {
        const order = compareIdentifiers(a.prerelease[i], b.prerelease[i]);
        if (order !== 0) return order;
    }
    return a.prerelease.length - b.prerelease.length;
}

/** Negative when `a` is older than `b`, positive when newer; undefined if either isn't a version. */
export function compareVersions(a: string, b: string): number | undefined {
    const parsedA = parseVersion(a);
    const parsedB = parseVersion(b);
    return parsedA && parsedB ? compareParsed(parsedA, parsedB) : undefined;
}

/**
 * The notes to show, newest first: those newer than the last version the user acknowledged, up to
 * the running build's version (notes written ahead of their release stay hidden). Someone who has
 * never acknowledged any (a first visit, or a visit from before the popup existed) only gets the
 * newest. A build without a proper version (development, a test image) has no upper limit, so
 * a newly added note shows up at the next reload. Notes whose version doesn't parse are ignored.
 */
export function selectUnseenNotes<T extends { version: string }>(
    notes: T[],
    currentVersion: string,
    lastSeenVersion: string | null,
): T[] {
    const current = parseVersion(currentVersion);
    const lastSeen = lastSeenVersion ? parseVersion(lastSeenVersion) : undefined;
    const unseen = notes
        .flatMap((note) => {
            const parsed = parseVersion(note.version);
            return parsed ? [{note, parsed}] : [];
        })
        .filter(({parsed}) =>
            (!current || compareParsed(parsed, current) <= 0) && (!lastSeen || compareParsed(parsed, lastSeen) > 0))
        .sort((a, b) => compareParsed(b.parsed, a.parsed))
        .map(({note}) => note);
    return lastSeen ? unseen : unseen.slice(0, 1);
}
